import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import classnames from 'classnames';
import { route } from 'router';
import { Trans } from 'react-i18next';
import { Button, Input, Text, Link, Checkbox, Collapse } from 'components-guide';
import Info from 'config/info';

import './Crossword.form.scss';

export interface ValueProps {
    /**
     * name value
     */
    name?: string;
    /**
     * last name value
     */
    lastName?: string;
    /**
     * email value
     */
    email?: string;
    /**
     * privacy value
     */
    privacyCrossword?: boolean;
    /**
     * newsletter value
     */
    newsletterCrossword?: boolean;
}

export interface CrosswordProps {
    /**
     * Form value
     */
    value?: ValueProps;
    /**
     * Loading
     */
    loading?: boolean;
    /**
     * Class name styles
     */
    className?: string;
    /**
     * Style
     */
    style?: any;
    /**
     * Translate prop
     */
    t: any;
    /**
     * Function called on submit form
     */
    onSubmit?(form: any): any;
}

const schema = (t: any) =>
    yup.object().shape({
        name: yup.string().required(t('validation.required', { field: t('forms.crossword.name.label') })),
        lastName: yup.string().required(t('validation.required', { field: t('forms.crossword.lastName.label') })),
        email: yup
            .string()
            .email(t('validation.email', { field: t('forms.crossword.email.label') }))
            .required(t('validation.required', { field: t('forms.crossword.email.label') })),
        privacyCrossword: yup.bool().oneOf([true], t('validation.required', { field: '' })),
        newsletterCrossword: yup.bool().oneOf([true], t('validation.required', { field: '' })),
    });

const defaultValue = {
    name: '',
    lastName: '',
    email: '',
    privacyCrossword: false,
    newsletterCrossword: false,
} as any;

const CrosswordForm: React.FC<CrosswordProps> = ({
    value = defaultValue,
    loading = false,
    className,
    style,
    t,
    onSubmit = () => {},
}) => {
    const { register, handleSubmit, control, formState: { errors } } = useForm({
        mode: 'onSubmit',
        resolver: yupResolver(schema(t)),
        defaultValues: value,
    });
    const [privacityViewMore, setPrivacityViewMore] = useState(false);
    const classNames = classnames(['crossword-form', className]);

    const handleClickPrivacy = () => window.open(route.app.legal.info(), '_blank');
    const handleOnClickViewMore = () => setPrivacityViewMore(!privacityViewMore);

    return (
        <form className={classNames} style={style} onSubmit={handleSubmit(onSubmit)} noValidate>
            <Text weight="semi">{t('components.crossword.formTitle')}</Text>
            <div className="form__row">
                <Input
                    className="form-control form__row--item"
                    type="text"
                    id="name"
                    data-cy="name"
                    material
                    label={`${t('forms.crossword.name.label')} *`}
                    error={errors.name?.['message']}
                    {...register('name')}
                />
                <Input
                    className="form-control form__row--item"
                    type="text"
                    id="lastName"
                    data-cy="lastName"
                    material
                    label={`${t('forms.crossword.lastName.label')} *`}
                    error={errors.lastName?.['message']}
                    {...register('lastName')}
                />
            </div>
            <Input
                className="form-control"
                type="email"
                id="email"
                data-cy="email"
                material
                label={`${t('forms.crossword.email.label')} *`}
                error={errors.email?.['message']}
                {...register('email')}
            />
            <div className="mb-1">
                <Controller
                    render={({ field, fieldState }: any) => (
                        <Checkbox
                            {...field}
                            id="privacyCrossword"
                            name="privacyCrossword"
                            defaultChecked={value.privacyCrossword}
                            error={!!errors.privacyCrossword?.['message']}
                        >
                            <Text tag="span">
                                <Trans i18nKey="forms.crossword.privacy.label">
                                    {t('forms.crossword.privacy.label')}
                                    <Link underline onClick={handleClickPrivacy} />
                                    <Link underline onClick={handleOnClickViewMore} className="link--legal" />
                                    &nbsp;*
                                </Trans>
                            </Text>
                        </Checkbox>
                    )}
                    name="privacyCrossword"
                    control={control}
                />
                <Collapse opened={privacityViewMore}>
                    <Text typo="small" className="mt-1">
                        <Trans i18nKey="forms.crossword.privacy.text">
                            {t('forms.crossword.privacy.text')}
                            <Link href={`mailto:${Info.emails.mortgages}`} rel="noopener noreferrer" target="_blank" />
                        </Trans>
                    </Text>
                </Collapse>
            </div>

            <div className="mb-1">
                <Controller
                    render={({ field, fieldState }: any) => (
                        <Checkbox
                            {...field}
                            id="newsletterCrossword"
                            name="newsletterCrossword"
                            defaultChecked={value.newsletterCrossword}
                            error={!!errors.newsletterCrossword?.['message']}
                        >
                            <Text tag="span">
                                <Trans i18nKey="forms.crossword.newsletter.label">
                                    {t('forms.crossword.newsletter.label')}
                                    <Link
                                        underline
                                        href="https://myinvestor.es/data/pdf/legal/Bases_legales_crucigramas_cine.pdf"
                                        target="_blank"
                                    />
                                    &nbsp;*
                                </Trans>
                            </Text>
                        </Checkbox>
                    )}
                    name="newsletterCrossword"
                    control={control}
                />
            </div>

            <div className="flex row-center">
                <Button block loading={loading} disabled={loading}>
                    {t('forms.crossword.submit')}
                </Button>
            </div>
        </form>
    );
};

export default CrosswordForm;
